/*!
 * Copyright © 2026. Verizon Connect Ireland Limited. All rights reserved.
 */

import { HttpErrorResponse } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { MatLegacyDialog } from '@angular/material/legacy-dialog';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { Action, Store } from '@ngrx/store';
import { CreateOverrideDto, GetSegmentInfoResponseDto, SegmentDirectionalSpeedLimitDto, UpdateOverrideDto } from 'customer-speed-api';
import moment from 'moment';
import { combineLatest, of } from 'rxjs';
import {
  catchError,
  concatMap,
  debounceTime,
  distinctUntilChanged,
  filter,
  map,
  mergeMap,
  switchMap,
  take,
  tap,
  withLatestFrom
} from 'rxjs/operators';
import { SnackbarService } from '../../../core/components/snackbar/service/snackbar.service';
import { EPendoCustomEvents, ESpeedUnits } from '../../../core/models';
import { delayedRetry } from '../../../core/operators';
import {
  CloseBalloon,
  CloseManagePostedSpeedPanel,
  EMapsActions,
  OpenManagePostedSpeedPanel,
  PanAndZoomToLocation
} from '../../../core/store/actions';
import {
  getAccountId,
  getMapModuleLoaded,
  getSpeedUnits,
  getUserUserName,
  getVehicleDriverPanelModuleLoaded
} from '../../../core/store/selectors';
import { getIsManagePostedSpeedOpen } from '../../../core/store/selectors/layout.selectors';
import { getMapBounds, getMapZoomLevel } from '../../../core/store/selectors/maps.selectors';
import { PendoTrackEventService } from '../../../external/services/pendo-track-event/pendo-track-event.service';
import { OverrideConflictDialogComponent } from '../../components/override-conflict-dialog/override-conflict-dialog.component';
import { RoadNotAvailableDialogComponent } from '../../components/road-not-available-dialog/road-not-available-dialog.component';
import { RoadTypeConflictDialogComponent } from '../../components/road-type-conflict-dialog/road-type-conflict-dialog.component';
import { EConflictDialogResult, EManagePostedSpeedTabs, EOverrideStatus, ISaveOverrideError, IUpdateSpeedLimitForm } from '../../models';
import { ManagePostedSpeedService } from '../../services';
import { buildPendoDataCompletenessPayload } from '../../utils';
import { hasDirectionConflict } from '../../utils/direction-conflict';
import * as ManagePostedSpeedActions from '../actions/manage-posted-speed.actions';
import {
  selectAllowMapRefine,
  selectEditingOverrideId,
  selectFilters,
  selectManagePostedSpeedState,
  selectPostedSpeedUpdates,
  selectSegmentInfo
} from '../selectors/manage-posted-speed.selectors';

/** Minimum zoom level for reliable road segment selection. */
const SEGMENT_SELECTION_MIN_ZOOM = 15;

@Injectable()
export class ManagePostedSpeedEffects {
  public triggerManagePostedSpeed$ = createEffect(() =>
    this._actions$.pipe(
      ofType(ManagePostedSpeedActions.triggerManagePostedSpeed),
      switchMap(({ mapCenter }) => [new CloseBalloon(), ManagePostedSpeedActions.openManagePostedSpeedPanel({ mapCenter })])
    )
  );

  public openManagePostedSpeedPanel$ = createEffect(() =>
    this._actions$.pipe(
      ofType(ManagePostedSpeedActions.openManagePostedSpeedPanel),
      switchMap(({ mapCenter }) => [ManagePostedSpeedActions.getSegmentInfo({ mapCenter }), ManagePostedSpeedActions.loadOverrides()])
    )
  );

  public getSegmentInfo$ = createEffect(() =>
    this._actions$.pipe(
      ofType(ManagePostedSpeedActions.getSegmentInfo),
      withLatestFrom(this._store.select(getMapZoomLevel)),
      switchMap(([{ mapCenter }, zoomLevel]) =>
        this._managePostedSpeedService.getSegmentInfoFull(mapCenter.lat, mapCenter.lng).pipe(
          delayedRetry(),
          switchMap(({ mapCenter: center, roadDetails, segmentInfo }) => {
            if (this._hasActiveOrPlannedOverride(segmentInfo)) {
              return [
                new OpenManagePostedSpeedPanel(),
                ManagePostedSpeedActions.showOverrideConflictDialog({ segmentInfo, source: 'selection' })
              ];
            }

            return [ManagePostedSpeedActions.getSegmentInfoSuccess({ mapCenter: center, roadDetails, segmentInfo })];
          }),
          catchError(error =>
            this._handleSegmentLookupError(
              error,
              zoomLevel,
              ManagePostedSpeedActions.getSegmentInfoFailure({ error, roadNotAvailable: this._isRoadNotAvailableError(error) })
            )
          )
        )
      )
    )
  );

  /**
   * Handles additional segment selection for multi-segment workflows.
   * Calls the segment API, checks for override conflicts, checks same-road
   * constraint, trims expired override geometry, and dispatches the
   * appropriate action.
   * Uses mergeMap to allow concurrent in-flight requests for different segments.
   */
  public selectAdditionalSegment$ = createEffect(() =>
    this._actions$.pipe(
      ofType(ManagePostedSpeedActions.selectAdditionalSegment),
      withLatestFrom(this._store.select(selectManagePostedSpeedState), this._store.select(getMapZoomLevel)),
      mergeMap(([{ mapCenter }, mpsState, zoomLevel]) =>
        this._managePostedSpeedService.getSegmentInfoFull(mapCenter.lat, mapCenter.lng).pipe(
          delayedRetry(),
          switchMap(({ roadDetails, segmentInfo }) => {
            if (this._hasActiveOrPlannedOverride(segmentInfo)) {
              return [ManagePostedSpeedActions.showOverrideConflictDialog({ segmentInfo, source: 'selection' })];
            }

            const existingSegments = Object.values(mpsState.selectedSegments);

            // Direction constraint: check for conflicts between existing segments and new segment
            if (existingSegments.length > 0) {
              const existingDirection = existingSegments[0].roadDetails.direction;
              if (hasDirectionConflict(existingDirection, roadDetails.direction)) {
                return [ManagePostedSpeedActions.showRoadTypeConflictDialog()];
              }
            }

            return [ManagePostedSpeedActions.addSegmentToSelection({ segmentInfo, roadDetails })];
          }),
          catchError(error =>
            this._handleSegmentLookupError(
              error,
              zoomLevel,
              ManagePostedSpeedActions.addSegmentToSelectionFailure({ error, roadNotAvailable: this._isRoadNotAvailableError(error) })
            )
          )
        )
      )
    )
  );

  public onGetSegmentInfoSuccess$ = createEffect(() =>
    this._actions$.pipe(
      ofType(ManagePostedSpeedActions.getSegmentInfoSuccess),
      switchMap(({ mapCenter }) => {
        const location = { lat: mapCenter.lat, lng: mapCenter.lng };
        return [new OpenManagePostedSpeedPanel(), new PanAndZoomToLocation(location)];
      })
    )
  );

  /**
   * Shows the "Road not available" dialog when a segment lookup returns 404
   * (no road in the map data). Covers both the open-panel flow and the
   * additional-segment selection flow. If the panel is already open it stays open.
   */
  public onRoadNotAvailable$ = createEffect(
    () =>
      this._actions$.pipe(
        ofType(ManagePostedSpeedActions.getSegmentInfoFailure, ManagePostedSpeedActions.addSegmentToSelectionFailure),
        filter(({ roadNotAvailable }) => roadNotAvailable),
        tap(() => {
          this._dialog.open(RoadNotAvailableDialogComponent, { panelClass: 'road-not-available-dialog' });
        })
      ),
    { dispatch: false }
  );

  /**
   * Always opens the panel on any getSegmentInfoFailure regardless of error type.
   */
  public onGetSegmentInfoFailure$ = createEffect(() =>
    this._actions$.pipe(
      ofType(ManagePostedSpeedActions.getSegmentInfoFailure),
      map(() => new OpenManagePostedSpeedPanel())
    )
  );

  public openManagePostedSpeedPanelFromDeeplink$ = createEffect(() =>
    this._actions$.pipe(
      ofType(ManagePostedSpeedActions.openManagePostedSpeedPanelFromDeeplink),
      switchMap(({ coordinates }) =>
        // Wait for map module and vehicle driver panel to load
        combineLatest([this._store.select(getMapModuleLoaded), this._store.select(getVehicleDriverPanelModuleLoaded)]).pipe(
          filter(([mapLoaded, panelLoaded]) => mapLoaded && panelLoaded),
          take(1),
          switchMap(() => [
            new PanAndZoomToLocation({ lat: coordinates.lat, lng: coordinates.lng }),
            ManagePostedSpeedActions.openManagePostedSpeedPanel({ mapCenter: coordinates })
          ])
        )
      )
    )
  );

  public closeManagePostedSpeedPanel$ = createEffect(() =>
    this._actions$.pipe(
      ofType(ManagePostedSpeedActions.closeManagePostedSpeedPanel),
      switchMap(() => [new CloseManagePostedSpeedPanel()])
    )
  );

  /**
   * Shows a persistent snackbar when the Manage Posted Speed panel is open and
   * the current zoom level is below the minimum threshold for segment selection.
   * The snackbar is dismissed as soon as zoom reaches the threshold or the panel closes.
   */
  public zoomInsufficientSnackbar$ = createEffect(
    () =>
      combineLatest([this._store.select(getIsManagePostedSpeedOpen), this._store.select(getMapZoomLevel)]).pipe(
        map(([isOpen, zoomLevel]) => isOpen && zoomLevel < SEGMENT_SELECTION_MIN_ZOOM),
        distinctUntilChanged(),
        tap(shouldShow => {
          if (shouldShow) {
            this._snackbarService.open($localize`:@@S_LIVEMAP_ZOOM_IN_TO_SELECT:Zoom in to select a road`);
          } else {
            this._snackbarService.close();
          }
        })
      ),
    { dispatch: false }
  );

  public loadOverrides$ = createEffect(() =>
    this._actions$.pipe(
      ofType(ManagePostedSpeedActions.loadOverrides),
      withLatestFrom(this._store.select(selectAllowMapRefine), this._store.select(getMapBounds), this._store.select(selectFilters)),
      switchMap(([, allowMapRefine, bounds, filters]) => {
        const overrides$ = allowMapRefine
          ? this._managePostedSpeedService.getOverrides(bounds, filters)
          : this._managePostedSpeedService.getAllOverrides(filters);
        return overrides$.pipe(
          map(result =>
            ManagePostedSpeedActions.loadOverridesSuccess({
              overrides: result.overrides,
              totalItems: result.totalItems,
              totalAccountOverrides: result.totalAccountOverrides
            })
          ),
          catchError(error => of(ManagePostedSpeedActions.loadOverridesFailure({ error })))
        );
      })
    )
  );

  public reloadOnMapRefineChange$ = createEffect(() =>
    this._actions$.pipe(
      ofType(ManagePostedSpeedActions.setAllowMapRefine),
      map(() => ManagePostedSpeedActions.loadOverrides())
    )
  );

  /**
   * Reloads overrides from the API when filters are applied or cleared.
   * Since filtering is now server-side, we need a fresh API call.
   */
  public reloadOnFiltersChange$ = createEffect(() =>
    this._actions$.pipe(
      ofType(ManagePostedSpeedActions.applyFilters, ManagePostedSpeedActions.clearFilters),
      map(() => ManagePostedSpeedActions.loadOverrides())
    )
  );

  /**
   * Reloads overrides when map bounds change and allowMapRefine is active.
   * Only triggers when the manage posted speed panel is open.
   * Debounces to avoid excessive API calls during panning.
   */
  public reloadOnBoundsChange$ = createEffect(() =>
    this._actions$.pipe(
      ofType(EMapsActions.MapBoundsUpdated),
      withLatestFrom(this._store.select(selectAllowMapRefine), this._store.select(getIsManagePostedSpeedOpen)),
      filter(([, allowMapRefine, isPanelOpen]) => allowMapRefine && isPanelOpen),
      debounceTime(500),
      map(() => ManagePostedSpeedActions.loadOverrides())
    )
  );

  public deletePostedSpeedUpdate$ = createEffect(() =>
    this._actions$.pipe(
      ofType(ManagePostedSpeedActions.deletePostedSpeedUpdate),
      switchMap(({ updateId }) =>
        this._managePostedSpeedService.deleteOverride(updateId).pipe(
          map(() => ManagePostedSpeedActions.deletePostedSpeedUpdateSuccess({ updateId })),
          catchError(error => of(ManagePostedSpeedActions.deletePostedSpeedUpdateFailure({ error })))
        )
      )
    )
  );

  public showOverrideConflictDialog$ = createEffect(() =>
    this._actions$.pipe(
      ofType(ManagePostedSpeedActions.showOverrideConflictDialog),
      switchMap(({ segmentInfo, source }) => {
        const dialogRef = this._dialog.open(OverrideConflictDialogComponent, {
          data: { segmentInfo, source },
          panelClass: 'override-conflict-dialog'
        });
        return dialogRef.afterClosed().pipe(
          filter((result: EConflictDialogResult) => result === EConflictDialogResult.Manage),
          switchMap(() => {
            const actions: Action[] = [];

            if (source === 'save') {
              actions.push(ManagePostedSpeedActions.closeUpdateSpeedLimitForm());
            }

            actions.push(ManagePostedSpeedActions.setActiveTab({ tab: EManagePostedSpeedTabs.EditDelete }));

            if (source === 'save') {
              const overrideId = this._extractOverrideIdFromLinks(segmentInfo);
              if (overrideId) {
                actions.push(ManagePostedSpeedActions.selectUpdateCard({ updateId: overrideId }));
              }
            }

            return actions;
          })
        );
      })
    )
  );

  /**
   * Opens the road type conflict dialog when a user tries to select a segment
   * with a conflicting direction (one-way vs two-way, or opposite one-way).
   * The dialog is informational only - it closes without further action.
   */
  public showRoadTypeConflictDialog$ = createEffect(
    () =>
      this._actions$.pipe(
        ofType(ManagePostedSpeedActions.showRoadTypeConflictDialog),
        switchMap(() => {
          const dialogRef = this._dialog.open(RoadTypeConflictDialogComponent, {
            panelClass: 'road-type-conflict-dialog'
          });
          return dialogRef.afterClosed();
        })
      ),
    { dispatch: false }
  );

  public trackPendoDataCompleteness$ = createEffect(
    () =>
      this._actions$.pipe(
        ofType(ManagePostedSpeedActions.getSegmentInfoSuccess, ManagePostedSpeedActions.addSegmentToSelection),
        withLatestFrom(this._store.select(selectManagePostedSpeedState)),
        tap(([action, mpsState]) => {
          const roadDetails = action.roadDetails;
          const mapCenter = 'mapCenter' in action ? action.mapCenter : mpsState.mapCenter;
          const payload = buildPendoDataCompletenessPayload(roadDetails, mapCenter, mpsState.selectedSegmentId);

          this._pendoTrackEventService.sendPendoTrackEventWhenReady(EPendoCustomEvents.ManagePostedSpeedRoadDetailsCard, payload);
        })
      ),
    { dispatch: false }
  );

  public trackPendoSaveOverrideOutcome$ = createEffect(
    () =>
      this._actions$.pipe(
        ofType(ManagePostedSpeedActions.saveOverrideSuccess, ManagePostedSpeedActions.saveOverrideFailure),
        withLatestFrom(this._store.select(getAccountId), this._store.select(getUserUserName), this._store.select(getSpeedUnits)),
        tap(([action, accountId, accountName, speedUnits]) => {
          try {
            if (action.type === ManagePostedSpeedActions.saveOverrideSuccess.type) {
              const { trackingPayload } = action as ReturnType<typeof ManagePostedSpeedActions.saveOverrideSuccess>;
              const unitType: string = speedUnits === ESpeedUnits.MilesPerHour ? 'mph' : 'kmh';
              const updatedPostedSpeed: number =
                speedUnits === ESpeedUnits.MilesPerHour
                  ? Math.round(trackingPayload.updatedPostedSpeedInKmh * 0.621371)
                  : trackingPayload.updatedPostedSpeedInKmh;
              const startDateType: string = this._isActiveStartDate(trackingPayload.startDate) ? 'Active' : 'Planned';

              this._pendoTrackEventService.sendPendoTrackEventWhenReady(EPendoCustomEvents.ManagePostedSpeedSaveOverrideSuccess, {
                Account_ID: accountId,
                Account_Name: accountName,
                Unit_Type: unitType,
                Number_of_Segments: trackingPayload.numberOfSegments,
                Start_Date_Type: startDateType,
                Updated_Posted_Speed: updatedPostedSpeed,
                Direction: trackingPayload.direction
              });
            } else {
              const { error } = action as ReturnType<typeof ManagePostedSpeedActions.saveOverrideFailure>;
              this._pendoTrackEventService.sendPendoTrackEventWhenReady(EPendoCustomEvents.ManagePostedSpeedSaveOverrideFailure, {
                Error_Type: this._classifySaveErrorType(error)
              });
            }
          } catch {
            // Tracking is non-critical; swallow errors to protect the effect stream
          }
        })
      ),
    { dispatch: false }
  );

  public trackPendoOverrideErrorEncountered$ = createEffect(
    () =>
      this._actions$.pipe(
        ofType(
          ManagePostedSpeedActions.showRoadTypeConflictDialog,
          ManagePostedSpeedActions.showOverrideConflictDialog,
          ManagePostedSpeedActions.getSegmentInfoFailure,
          ManagePostedSpeedActions.addSegmentToSelectionFailure
        ),
        tap(action => {
          try {
            // Save-originated conflicts are reported by trackPendoSaveOverrideOutcome$ as a
            // SaveOverrideFailure (Duplicate_Active_Edit). Skip here to avoid double counting.
            if (
              action.type === ManagePostedSpeedActions.showOverrideConflictDialog.type &&
              (action as ReturnType<typeof ManagePostedSpeedActions.showOverrideConflictDialog>).source === 'save'
            ) {
              return;
            }

            let errorType: string;
            if (action.type === ManagePostedSpeedActions.showRoadTypeConflictDialog.type) {
              errorType = 'Mixed_Road_Types';
            } else if (action.type === ManagePostedSpeedActions.showOverrideConflictDialog.type) {
              errorType = 'Duplicate_Active_Edit';
            } else if ((action as ReturnType<typeof ManagePostedSpeedActions.getSegmentInfoFailure>).roadNotAvailable) {
              // Only the two segment-lookup failures reach this branch, and a 404 among them means
              // no road in the map data - an expected user situation, not a system failure.
              errorType = 'Road_Not_Available';
            } else {
              errorType = 'System_Failure';
            }
            this._pendoTrackEventService.sendPendoTrackEventWhenReady(EPendoCustomEvents.ManagePostedSpeedOverrideErrorEncountered, {
              Error_Type: errorType
            });
          } catch {
            // Tracking is non-critical; swallow errors to protect the effect stream
          }
        })
      ),
    { dispatch: false }
  );

  public saveOverride$ = createEffect(() =>
    this._actions$.pipe(
      ofType(ManagePostedSpeedActions.saveOverride),
      withLatestFrom(
        this._store.select(selectEditingOverrideId),
        this._store.select(selectSegmentInfo),
        this._store.select(selectPostedSpeedUpdates),
        this._store.select(selectManagePostedSpeedState),
        this._store.select(getSpeedUnits)
      ),
      switchMap(([{ formData }, editingOverrideId, segmentInfo, postedSpeedUpdates, mpsState, speedUnits]) => {
        if (formData.updatedPostedSpeedLimit == null || !formData.startDate) {
          return of(ManagePostedSpeedActions.saveOverrideFailure({ error: 'Cannot save override: missing required form data' }));
        }

        const speedLimit = this._convertToKmh(formData.updatedPostedSpeedLimit, speedUnits);
        const startDate = formData.startDate;

        if (editingOverrideId != null) {
          const existingOverride = postedSpeedUpdates.find(u => u.id === editingOverrideId);
          if (!existingOverride?.segmentRef) {
            return of(ManagePostedSpeedActions.saveOverrideFailure({ error: 'Cannot update override: missing segment reference' }));
          }

          const updateDto = this._buildUpdateDto(formData, existingOverride, speedLimit, startDate);
          return this._managePostedSpeedService.updateOverride(editingOverrideId, updateDto).pipe(
            switchMap(() => [
              ManagePostedSpeedActions.saveOverrideSuccess({
                trackingPayload: {
                  updatedPostedSpeedInKmh: speedLimit,
                  direction: formData.directionOfEdit,
                  numberOfSegments: 1,
                  startDate
                }
              }),
              ManagePostedSpeedActions.loadOverrides()
            ]),
            catchError(error => of(...this._buildSaveErrorActions(error, segmentInfo)))
          );
        }

        // Collect segment refs in spatial order from computed segment groups.
        // Each group's segments are already ordered by the contiguity engine.
        // We create one override per contiguous group (API validates adjacency).
        const groups = mpsState.segmentGroups;
        if (groups.length === 0) {
          return of(ManagePostedSpeedActions.saveOverrideFailure({ error: 'Cannot create override: no segment selected' }));
        }

        const roadName = groups[0].mergedRoadDetails.fullAddress || 'Speed Override';

        // Create one override per contiguous group
        const createDtos: CreateOverrideDto[] = groups.map(group => ({
          name: roadName,
          hereSegmentRefs: group.segments.map(seg => seg.segmentRef).filter((ref): ref is string => !!ref),
          notes: formData.notes || null,
          direction: formData.directionOfEdit.toUpperCase() as CreateOverrideDto.DirectionEnum,
          speedOverwriteInKmh: speedLimit,
          startDate: this._toStartOfDayLocal(startDate),
          endDate: formData.endDate ? this._toEndOfDayLocal(formData.endDate) : null,
          cityName: group.mergedRoadDetails.cityName ?? null
        }));

        // Execute all create calls sequentially
        return createDtos
          .reduce((chain$, dto) => chain$.pipe(concatMap(() => this._managePostedSpeedService.createOverride(dto))), of(null as any))
          .pipe(
            switchMap(() => [
              ManagePostedSpeedActions.saveOverrideSuccess({
                trackingPayload: {
                  updatedPostedSpeedInKmh: speedLimit,
                  direction: formData.directionOfEdit,
                  numberOfSegments: groups.reduce((total, group) => total + group.segments.length, 0),
                  startDate
                }
              }),
              ManagePostedSpeedActions.loadOverrides()
            ]),
            catchError(error => of(...this._buildSaveErrorActions(error, segmentInfo)))
          );
      })
    )
  );

  constructor(
    private readonly _actions$: Actions,
    private readonly _store: Store,
    private readonly _managePostedSpeedService: ManagePostedSpeedService,
    private readonly _dialog: MatLegacyDialog,
    private readonly _pendoTrackEventService: PendoTrackEventService,
    private readonly _snackbarService: SnackbarService
  ) {}

  /**
   * Returns true when the segment lookup error means there is no road at the clicked
   * location (HTTP 404 from GET /v1/segments). Any other error is treated as a
   * transient/system failure rather than "road not available".
   */
  private _isRoadNotAvailableError(error: unknown): boolean {
    return error instanceof HttpErrorResponse && error.status === 404;
  }

  private _handleSegmentLookupError(error: unknown, zoomLevel: number, fallbackAction: Action) {
    return of(
      this._isRoadNotAvailableError(error) && zoomLevel < SEGMENT_SELECTION_MIN_ZOOM
        ? (new OpenManagePostedSpeedPanel() as Action)
        : fallbackAction
    );
  }

  /**
   * Builds an UpdateOverrideDto from the form data for the PUT endpoint.
   * The backend now accepts a flat structure and handles BOTH expansion internally.
   */
  private _buildUpdateDto(
    formData: IUpdateSpeedLimitForm,
    existingOverride: { roadName: string | null },
    speedLimit: number,
    startDate: string
  ): UpdateOverrideDto {
    return {
      name: existingOverride.roadName || 'Speed Override',
      direction: formData.directionOfEdit.toUpperCase() as UpdateOverrideDto.DirectionEnum,
      speedOverwriteInKmh: speedLimit,
      startDate: this._toStartOfDayLocal(startDate),
      endDate: formData.endDate ? this._toEndOfDayLocal(formData.endDate) : null,
      notes: formData.notes || null
    };
  }

  /**
   * Converts a date-only string (YYYY-MM-DD) to a start-of-day DateTimeOffset
   * in the customer's local timezone (e.g., "2026-05-06T00:00:00+02:00").
   */
  private _toStartOfDayLocal(dateStr: string): string {
    return moment(dateStr).startOf('day').format();
  }

  /**
   * Converts a date-only string (YYYY-MM-DD) to an end-of-day DateTimeOffset
   * in the customer's local timezone (e.g., "2026-05-06T23:59:59+02:00").
   */
  private _toEndOfDayLocal(dateStr: string): string {
    return moment(dateStr).endOf('day').format();
  }

  /**
   * Converts a speed value from the user's display unit to km/h for the API.
   * The API always expects speedOverwriteInKmh, but the user enters the value
   * in their configured unit (mph, knots, etc.).
   */
  private _convertToKmh(speed: number, speedUnits: ESpeedUnits): number {
    switch (speedUnits) {
      case ESpeedUnits.MilesPerHour:
        return Math.round(speed / 0.621371);
      case ESpeedUnits.Knots:
        return Math.round(speed / 0.539957);
      case ESpeedUnits.CentimetersPerSecond:
        return Math.round(speed / 27.77777778);
      case ESpeedUnits.KilometersPerHour:
      default:
        return speed;
    }
  }

  /**
   * Checks if any directional speed limit has an active or planned customer override.
   * Detection relies solely on the presence of `speedSource === CUSTOMER_OVERRIDE`
   * in `directionalSpeedLimits`. The HATEOAS `links` array is a navigation concern
   * and is not used as an indicator of override lifecycle status.
   */
  private _hasActiveOrPlannedOverride(segmentInfo: GetSegmentInfoResponseDto): boolean {
    return (
      segmentInfo.directionalSpeedLimits?.some(
        limit => limit.speedSource === SegmentDirectionalSpeedLimitDto.SpeedSourceEnum.CustomerOverride
      ) ?? false
    );
  }

  /**
   * Classifies the override state of a segment returned by the API.
   * Returns {@link EOverrideStatus.ACTIVE} when any directional speed limit has
   * `speedSource = CUSTOMER_OVERRIDE`, otherwise {@link EOverrideStatus.NONE}.
   * The `links` field is not consulted - it is a HATEOAS navigation concern and
   * has no semantic relationship to override lifecycle status.
   */
  _classifyOverrideState(segmentInfo: GetSegmentInfoResponseDto): EOverrideStatus {
    const hasCustomerOverride: boolean =
      segmentInfo.directionalSpeedLimits?.some(
        limit => limit.speedSource === SegmentDirectionalSpeedLimitDto.SpeedSourceEnum.CustomerOverride
      ) ?? false;

    return hasCustomerOverride ? EOverrideStatus.ACTIVE : EOverrideStatus.NONE;
  }

  /**
   * Determines if a start date represents an "Active" override (today or in the past)
   * or a "Planned" override (in the future).
   */
  private _isActiveStartDate(startDate: string): boolean {
    return moment(startDate).startOf('day').isSameOrBefore(moment().startOf('day'));
  }

  /**
   * Classifies a save-override failure into a Pendo `Error_Type`.
   * - string errors come from the pre-API form/segment guards → `Validation_Error`
   * - HTTP 409 from the API → `Duplicate_Active_Edit`
   * - anything else (network/5xx/unknown) → `System_Failure`
   */
  private _classifySaveErrorType(error: unknown): string {
    if (typeof error === 'string') {
      return 'Validation_Error';
    }
    if (typeof error === 'object' && error !== null && (error as { status?: number }).status === 409) {
      return 'Duplicate_Active_Edit';
    }
    return 'System_Failure';
  }

  /**
   * Builds the dispatch list for a saveOverride failure.
   * On HTTP 409 Conflict, surfaces the override conflict dialog (with CANCEL and
   * MANAGE UPDATE options) when segment info is available, while still dispatching
   * `saveOverrideFailure` to preserve Pendo tracking for the failure outcome.
   * For all other errors, dispatches only `saveOverrideFailure`.
   */
  private _buildSaveErrorActions(error: ISaveOverrideError | string, segmentInfo: GetSegmentInfoResponseDto | null): Action[] {
    const isConflict: boolean = typeof error === 'object' && error.status === 409;

    if (isConflict && segmentInfo != null) {
      return [
        ManagePostedSpeedActions.showOverrideConflictDialog({ segmentInfo, source: 'save' }),
        ManagePostedSpeedActions.saveOverrideFailure({ error })
      ];
    }

    return [ManagePostedSpeedActions.saveOverrideFailure({ error })];
  }

  /**
   * Extracts the override ID from segment info links.
   * The API returns a link with rel='override' and href='/overrides/{id}'.
   */
  private _extractOverrideIdFromLinks(segmentInfo: GetSegmentInfoResponseDto): string | null {
    const overrideLink = segmentInfo.links?.find((link: { rel?: string; href?: string }) => link.rel === 'override');
    if (!overrideLink?.href) {
      return null;
    }
    const parts = overrideLink.href.split('/');
    return parts[parts.length - 1] || null;
  }
}
